import {
  BookingStatusChip,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  PaymentStatusChip,
  Separator,
  Skeleton,
} from '@zuruflex/ui';
import { allowedTransitions } from '@zuruflex/core';
import type { BookingStatus } from '@zuruflex/types';
import { formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import { formatPhone } from '@zuruflex/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

/** A payment row as the staff booking route returns it. */
interface PaymentRow {
  id: string;
  method: string;
  provider: string | null;
  status: string;
  amount: number;
  currency: string;
  paidAt: string | null;
  claimedAt: string | null;
  claimNote: string | null;
  transactionId: string | null;
  createdAt: string;
}

interface PriceLine {
  /** Key under `common:priceLine`; the text is rendered here, not stored. */
  labelKey: string;
  labelArgs?: Record<string, string | number>;
  amount: number;
  kind: string;
}

export function BookingDetailPage() {
  const { reference = '' } = useParams();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);

  const { data: booking, isPending, error, refetch } = useQuery({
    queryKey: ['booking', reference],
    queryFn: () => api.getBooking(reference),
  });
  const [approving, setApproving] = useState(false);

  const changeStatus = useMutation({
    mutationFn: (status: BookingStatus) => api.updateBookingStatus(reference, status),
    onSuccess: () => {
      // The vehicle's own status moves with the booking, so both caches go.
      void queryClient.invalidateQueries({ queryKey: ['booking', reference] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending) return <Skeleton className="h-96 w-full" />;

  const record = booking as typeof booking & {
    customer?: { fullName: string | null; phone: string; email: string | null };
    vehicle?: { make: string; model: string; plateNumber: string };
    priceLines?: PriceLine[];
    payments?: PaymentRow[];
  };
  const payments = [...(record.payments ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  const claim = payments.find((p) => p.provider === 'manual' && p.status === 'UNPAID' && p.claimedAt);

  const next = allowedTransitions(booking.status);

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/bookings"
            className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
          >
            <ChevronLeft className="size-4" />
            {t('bookings.backToBookings')}
          </Link>
        }
        title={booking.reference}
        description={t('bookings.created', { when: formatDateTimeIn(booking.createdAt, locale) })}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <BookingStatusChip status={booking.status} label={t(`common:bookingStatus.${booking.status}`)} />
            {/* Hand-sent MoMo is approved here rather than by "Mark as
                confirmed", which confirms without recording any money. */}
            {booking.status === 'AWAITING_PAYMENT' ? (
              <Button size="sm" onClick={() => setApproving(true)}>
                {t('bookings.momo.approve')}
              </Button>
            ) : null}
            {next.map((status) => (
              <Button
                key={status}
                size="sm"
                variant={status === 'CANCELLED' ? 'outline' : 'default'}
                isLoading={changeStatus.isPending}
                onClick={() => changeStatus.mutate(status)}
              >
                {t('bookings.markAs', { status: t(`common:bookingStatus.${status}`).toLowerCase() })}
              </Button>
            ))}
          </div>
        }
      />

      {changeStatus.error ? (
        <ErrorState error={changeStatus.error} className="mb-6" />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('bookings.hire')}</CardTitle>
            <PaymentStatusChip status={booking.paymentStatus} label={t(`common:paymentStatus.${booking.paymentStatus}`)} />
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
              <Field
                label={t('common:field.vehicle')}
                value={record.vehicle ? `${record.vehicle.make} ${record.vehicle.model}` : '—'}
              />
              <Field label={t('common:field.plate')} value={record.vehicle?.plateNumber ?? '—'} mono />
              <Field label={t('common:field.days')} value={String(booking.days)} />
              <Field label={t('common:field.pickUp')} value={formatDateTimeIn(booking.pickupAt, locale)} />
              <Field label={t('common:field.dropOff')} value={formatDateTimeIn(booking.dropoffAt, locale)} />
              <Field label={t('common:field.dailyRate')} value={formatMoneyIn(booking.dailyRate, booking.currency, locale)} />
              {/* `dailyRate` above is the car alone while the subtotal
                  includes the driver, so on a chauffeured hire the two do not
                  multiply out. Naming the driver here is what makes that read
                  as the arithmetic it is rather than as a discrepancy — and
                  it is what the handover actually involves. */}
              {booking.withDriver ? (
                <Field
                  label={t('common:field.driver')}
                  value={formatMoneyIn(booking.driverDailyRate, booking.currency, locale)}
                />
              ) : null}
            </dl>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardHeader>
            <CardTitle>{t('bookings.price')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2.5 text-sm">
              {(record.priceLines ?? []).map((line, index) => (
                <div key={`${line.labelKey}-${index}`} className="flex justify-between gap-4">
                  <dt className="text-text-muted">
                    {t(`common:${line.labelKey}`, { ...line.labelArgs })}
                  </dt>
                  <dd className="tabular-nums">{formatMoneyIn(line.amount, booking.currency, locale)}</dd>
                </div>
              ))}
            </dl>
            <Separator className="my-4" />
            <div className="flex items-baseline justify-between">
              <span className="font-display font-bold">{t('common:field.total')}</span>
              <span className="font-display text-xl font-bold tabular-nums">
                {formatMoneyIn(booking.total, booking.currency, locale)}
              </span>
            </div>

            {/* Who paid for the promo is what the split below turns on: a
                Zuruflex promo leaves the partner's earnings on the full price,
                a partner's own leaves Zuruflex's commission on it. Without the
                funder beside it, a negative commission reads as a bug. */}
            {booking.promoDiscount ? (
              <>
                <Separator className="my-4" />
                <dl className="space-y-2.5 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-text-muted">{t('bookings.promo')}</dt>
                    <dd className="text-right">
                      {booking.promotionId ? (
                        <Link
                          to={`/promotions/${booking.promotionId}`}
                          className="font-mono font-semibold underline-offset-2 hover:underline"
                        >
                          {booking.promoCode ?? t('bookings.promoAutomatic')}
                        </Link>
                      ) : (
                        <span className="font-mono font-semibold">
                          {booking.promoCode ?? t('bookings.promoAutomatic')}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-text-muted">{t('bookings.promoDiscount')}</dt>
                    <dd className="tabular-nums">
                      {formatMoneyIn(booking.promoDiscount, booking.currency, locale)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-text-muted">{t('bookings.promoPaidBy')}</dt>
                    <dd>
                      {booking.promoFunder === 'PARTNER'
                        ? t('bookings.promoFunder.PARTNER')
                        : t('bookings.promoFunder.ZURUFLEX')}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-text-muted">{t('bookings.commission')}</dt>
                    <dd className="tabular-nums">
                      {formatMoneyIn(booking.commissionAmount, booking.currency, locale)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-text-muted">{t('bookings.partnerEarnings')}</dt>
                    <dd className="tabular-nums">
                      {formatMoneyIn(booking.partnerEarnings, booking.currency, locale)}
                    </dd>
                  </div>
                </dl>
              </>
            ) : null}
          </CardContent>
        </Card>

        {payments.length > 0 ? (
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>{t('bookings.momo.payments')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-border text-sm">
                {payments.map((payment) => (
                  <li key={payment.id} className="flex flex-wrap items-start justify-between gap-4 py-3">
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium">
                        {formatMoneyIn(payment.amount, payment.currency, locale)}
                        <span className="ml-2 text-text-muted">
                          {payment.provider ?? '—'} · {payment.method}
                        </span>
                      </p>
                      {payment.claimedAt ? (
                        <p className="text-text-muted">
                          {t('bookings.momo.claimed', {
                            when: formatDateTimeIn(payment.claimedAt, locale),
                          })}
                        </p>
                      ) : null}
                      {payment.claimNote ? (
                        <p className="whitespace-pre-wrap rounded-md bg-surface-sunken px-3 py-2 font-mono text-xs">
                          {payment.claimNote}
                        </p>
                      ) : null}
                      {payment.transactionId ? (
                        <p className="text-text-muted">
                          {t('bookings.momo.transaction', { id: payment.transactionId })}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <PaymentStatusChip
                        status={payment.status as never}
                        label={t(`common:paymentStatus.${payment.status}`)}
                      />
                      {payment.paidAt ? (
                        <span className="text-xs text-text-muted">
                          {formatDateTimeIn(payment.paidAt, locale)}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>{t('bookings.customer')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
              <Field label={t('common:field.name')} value={record.customer?.fullName ?? t('bookings.notCaptured')} />
              <Field
                label={t('common:field.phone')}
                value={record.customer ? formatPhone(record.customer.phone) : '—'}
                mono
              />
              <Field label={t('common:field.email')} value={record.customer?.email ?? '—'} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {approving ? (
        <ApproveMomoDialog
          reference={booking.reference}
          amount={claim ? formatMoneyIn(claim.amount, claim.currency, locale) : null}
          claimNote={claim?.claimNote ?? null}
          onClose={() => setApproving(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Approving a MoMo payment sent by hand to our number.
 *
 * Only once the money has been seen in the MoMo account: this confirms
 * the booking and tells the customer and the owner it is paid, and none
 * of that can be taken back from here.
 */
function ApproveMomoDialog({
  reference,
  amount,
  claimNote,
  onClose,
}: {
  reference: string;
  /** What the customer was asked for, when they have claimed. */
  amount: string | null;
  claimNote: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');

  const approve = useMutation({
    mutationFn: () =>
      api.approveManualPayment(reference, {
        transactionId: transactionId.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking', reference] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bookings.momo.approveTitle', { reference })}</DialogTitle>
          <DialogDescription>{t('bookings.momo.approveBody')}</DialogDescription>
        </DialogHeader>

        {claimNote ? (
          <div className="space-y-2 text-sm">
            {amount ? <p>{t('bookings.momo.expected', { amount })}</p> : null}
            <p className="whitespace-pre-wrap rounded-md bg-surface-sunken px-3 py-2 font-mono text-xs">
              {claimNote}
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-muted">{t('bookings.momo.noClaim')}</p>
        )}

        <div className="space-y-4">
          <FormField label={t('bookings.momo.transactionId')} hint={t('bookings.momo.transactionHint')}>
            {(field) => (
              <Input
                {...field}
                value={transactionId}
                maxLength={64}
                onChange={(event) => setTransactionId(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('bookings.momo.note')}>
            {(field) => (
              <Input
                {...field}
                value={note}
                maxLength={500}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </FormField>
        </div>

        {approve.error ? <ErrorState error={approve.error} /> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button isLoading={approve.isPending} onClick={() => approve.mutate()}>
            {t('bookings.momo.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{label}</dt>
      <dd className={`mt-1 text-sm font-medium ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
